import { Suspense, useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Bot,
  Database,
  Settings,
  Moon,
  Sun,
  Wrench,
  User,
  LogOut,
  CreditCard,
  ChevronDown,
  Bell,
  Clock,
  BarChart3,
  Menu,
  ShieldCheck,
  ScrollText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTheme } from "next-themes";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { TopbarStepper } from "@/components/ModernStepper";
import { VersionChip } from "@/components/VersionChip";
import { useStepperContext } from "@/contexts/StepperContext";
import { Logo } from "@/components/Logo";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarProvider,
  useSidebar,
} from "@/components/ui/sidebar";

import { authApi, User as AuthUser } from "@/services/authApi";
import { lazyWithReload } from "@/lib/lazy-with-reload";

// The assistant renders markdown tables/charts via RichMessageRenderer,
// which pulls recharts. Loading it in its own chunk keeps the shell's
// first paint free of ~600 KB of charting code.
const SynqAIAssistant = lazyWithReload(() =>
  import("@/components/SynqAIAssistant").then((m) => ({ default: m.SynqAIAssistant })),
);
import { ImpersonationBanner } from "@/components/ImpersonationBanner";

const navigation = [
  { name: "SynQAI", href: "/", icon: Bot, iconColor: "text-[hsl(225,84%,55%)]" },
  { name: "Study", href: "/study", icon: LayoutDashboard, iconColor: "text-[hsl(158,70%,45%)]" },
  { name: "Foundry", href: "/foundry", icon: Database, iconColor: "text-[hsl(280,65%,55%)]" },
  { name: "Reports", href: "/reports", icon: BarChart3, iconColor: "text-[hsl(142,72%,29%)]" },
  { name: "Jobs", href: "/jobs", icon: Clock, iconColor: "text-[hsl(32,95%,44%)]" },
];

const AppSidebar = () => {
  const { theme, setTheme } = useTheme();
  const location = useLocation();
  const currentPath = location.pathname;
  const { setOpen, setOpenMobile } = useSidebar();

  // Collapse the sidebar whenever a nav item is clicked. We close both
  // the desktop (`setOpen`) and mobile (`setOpenMobile`) state because
  // shadcn's <Sidebar collapsible="offcanvas"> tracks them separately —
  // closing only one leaves the drawer open on the other viewport.
  const collapseSidebar = () => {
    setOpen(false);
    setOpenMobile(false);
  };

  // Pages that want the nav out of the way announce it with a
  // `collapseSidebar` window event. Twelve of them already did — every
  // planning workspace and the forecasting wizard — and nothing was
  // listening, so the sidebar never moved and the calls did nothing.
  // This is the missing receiver.
  //
  // Deliberately not tied to the route: a page decides when it wants the
  // space, which is not always on arrival. The forecasting wizard, for
  // one, wants it on reaching Results, several steps after mount.
  useEffect(() => {
    window.addEventListener("collapseSidebar", collapseSidebar);
    return () => window.removeEventListener("collapseSidebar", collapseSidebar);
    // setOpen/setOpenMobile are stable from the sidebar context.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [isSuperUser, setIsSuperUser] = useState(false);
  const [isTenantAdmin, setIsTenantAdmin] = useState(false);

  // Pull the active user once on mount so we can decide which admin
  // items to surface. Failure is non-fatal — BE re-checks on every call.
  useEffect(() => {
    let cancelled = false;
    authApi.getMe().then(
      (u) => {
        if (cancelled) return;
        setIsSuperUser(!!u?.is_superuser);
        setIsTenantAdmin(!!u?.is_tenant_admin);
      },
      () => { /* swallow — non-essential */ },
    );
    return () => { cancelled = true; };
  }, []);

  // Audit Log is visible to tenant admins AND platform admins. Platform
  // Admin is superuser-only. Order matters: less-privileged items first.
  const navItems = [
    ...navigation,
    ...((isTenantAdmin || isSuperUser)
      ? [{ name: "Audit Log", href: "/admin/audit-log", icon: ScrollText, iconColor: "text-[hsl(38,92%,50%)]" }]
      : []),
    ...(isSuperUser
      ? [{ name: "Admin", href: "/admin/platform", icon: ShieldCheck, iconColor: "text-[hsl(38,92%,50%)]" }]
      : []),
  ];

  const isActive = (path: string) => currentPath === path;

  return (
    <Sidebar
      className="border-r border-border w-56 top-[var(--impersonation-height,0px)] h-[calc(100svh-var(--impersonation-height,0px))]"
      collapsible="offcanvas"
    >
      {/* Logo Header */}
      <div className="flex h-16 items-center justify-center px-4 border-b border-border bg-sidebar text-sidebar-foreground">
        <Link to="/" aria-label="UpSynQ home" className="inline-flex items-center">
          <Logo size="text-2xl" />
        </Link>
      </div>

      <SidebarContent className="px-3 py-6">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu className="space-y-2">
              {navItems.map((item) => (
                <SidebarMenuItem key={item.name}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive(item.href)}
                    className={cn(
                      "px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150",
                      "hover:bg-muted-hover",
                      isActive(item.href) && "bg-primary/10 text-primary font-semibold hover:bg-primary/10"
                    )}
                  >
                    <NavLink to={item.href} onClick={collapseSidebar}>
                      <item.icon className={cn("h-5 w-5 mr-3", item.iconColor)} />
                      <span>{item.name}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Theme Switcher */}
        <div className="absolute bottom-6 left-3 right-3">
          <Button
            variant="ghost"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="w-full justify-start hover:bg-muted-hover transition-all duration-300"
          >
            {theme === "dark" ? (
              <Sun className="h-4 w-4 mr-2" />
            ) : (
              <Moon className="h-4 w-4 mr-2" />
            )}
            <span>Toggle Theme</span>
          </Button>
        </div>
      </SidebarContent>
    </Sidebar>
  );
};

const TopBar = ({ topbarRef }: { topbarRef: React.RefObject<HTMLDivElement> }) => {
  const navigate = useNavigate();
  const { steps, title, showStepper, onStepClick } = useStepperContext();
  const { toggleSidebar } = useSidebar();

  const handleLogout = () => {
    navigate('/login');
  };


  const [userInfo, setUserInfo] = useState<{ name: string; company: string; avatar: string }>({
    name: "User",
    company: "Company",
    avatar: "U"
  });

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const user = await authApi.getMe();
        if (user) {
          // Get initials
          const initials = user.name
            ? user.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
            : user.email.substring(0, 2).toUpperCase();

          setUserInfo({
            name: user.name || user.email.split('@')[0],
            company: user.tenant_code || user.company || "Company",
            avatar: initials
          });
        }
      } catch (err) {
        console.error("Failed to fetch user info", err);
      }
    };

    fetchUser();
  }, []);

  const notifications = [
    { id: 1, title: "Forecast job complete", time: "2 min ago", type: "success" },
    { id: 2, title: "Foundry Data Synced", time: "5 min ago", type: "success" },
    { id: 3, title: "Data health alert: Missing values detected", time: "10 min ago", type: "warning" },
    { id: 4, title: "Production scheduling completed", time: "15 min ago", type: "success" },
  ];

  return (
    <div ref={topbarRef} className="fixed top-[var(--impersonation-height,0px)] left-0 right-0 z-40 h-16 border-b border-border bg-card flex items-center justify-between px-4">
      {/* Left side - Hamburger + Stepper */}
      <div className="flex items-center gap-4 justify-start">
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleSidebar}
          className="h-10 w-10 p-0 hover:bg-muted-hover"
        >
          <Menu className="h-5 w-5" />
        </Button>

        {showStepper && (
          <TopbarStepper steps={steps} title={title} onStepClick={onStepClick} />
        )}
      </div>

      {/* Right side - Version chip, Notifications and User dropdown */}
      <div className="flex items-center gap-2">
        {/* Tiny build-SHA chip — confirms FE / prajna / forecast-svc
            deploys at a glance. Sits to the left of the bell so it's
            visible without being noisy. */}
        <div className="hidden md:flex">
          <VersionChip />
        </div>
        {/* Notifications */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="relative h-10 w-10 p-0 hover:bg-muted-hover">
              <Bell className="h-5 w-5" />
              {notifications.length > 0 && (
                <span className="absolute -top-1 -right-1 h-5 w-5 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center font-medium">
                  {notifications.length}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72">
            <div className="px-3 py-2 border-b border-border">
              <h3 className="font-semibold text-sm">Notifications</h3>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {notifications.map((notification) => (
                <DropdownMenuItem key={notification.id} className="flex flex-col items-start p-3 cursor-pointer">
                  <div className="flex items-start justify-between w-full">
                    <div className="flex-1">
                      <p className="text-sm font-medium">{notification.title}</p>
                      <p className="text-xs text-muted-foreground mt-1">{notification.time}</p>
                    </div>
                    <div className={cn(
                      "w-2 h-2 rounded-full mt-1",
                      notification.type === "success" ? "bg-success" : "bg-warning"
                    )} />
                  </div>
                </DropdownMenuItem>
              ))}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-3 h-10 hover:bg-muted-hover">
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                <span className="text-primary-foreground font-medium text-sm">{userInfo.avatar}</span>
              </div>
              <div className="hidden md:block text-left">
                <div className="text-sm font-medium">{userInfo.name}</div>
                <div className="text-xs text-muted-foreground">{userInfo.company}</div>
              </div>
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem asChild>
              <Link to="/profile" className="flex items-center cursor-pointer">
                <User className="mr-2 h-4 w-4" />
                Profile
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/settings" className="flex items-center cursor-pointer">
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/billing" className="flex items-center cursor-pointer">
                <CreditCard className="mr-2 h-4 w-4" />
                Billing
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-destructive cursor-pointer">
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export const Layout = () => {
  const topbarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateVar = () => {
      const h = topbarRef.current?.offsetHeight ?? 64;
      // Only this bar's own height — index.css adds the impersonation
      // banner to produce --topbar-height. Setting the total here instead
      // would write an inline value that shadows that calc.
      document.documentElement.style.setProperty("--topbar-own-height", `${h}px`);
    };
    updateVar();
    window.addEventListener("resize", updateVar);
    return () => window.removeEventListener("resize", updateVar);
  }, []);

  return (
    <SidebarProvider defaultOpen={true}>
      <ImpersonationBanner />
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar />

        <div className="flex-1 min-w-0 flex flex-col">
          <TopBar topbarRef={topbarRef} />

          {/* Main content area with padding for fixed header */}
          <main className="flex-1 min-w-0 overflow-x-hidden" style={{ paddingTop: "var(--topbar-height, 64px)" }}>
            <Outlet />
          </main>
        </div>
      </div>
      {/* SynqAI Assistant — global floating button */}
      <Suspense fallback={null}>
        <SynqAIAssistant />
      </Suspense>
    </SidebarProvider>
  );
};